/* AuditPolicyDetailsViewRenderer.js */

const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;

const formatters = require("./formatters/index");
const textEditor = require("./slickeditors/InputTextEditor");
const checkboxEditor = require("./slickeditors/InputCheckboxEditor");
const childViewComponent = require("../../../../widgets/childview/ChildViewRenderer");
const editPolicyView = require("./subview/editAuditPolicy/EditAuditPolicyComponent");
const FileName = require("@gluenetworks/steve_utilities").fileName;
const JsonEditor = require("@gluenetworks/json-editor");
const {ruleUuidsToLabels} = require("../../utils/ruleLabelMapper");
const {buildPolicySchemaEntries} = require("../../schemas");
const {buildDynamicSchemas} = require("../../utils/buildDynamicSchemas");
const {buildCustomFieldEnumValidator} = require("../../utils/customFieldEnumValidator");
const {buildQueryBuilderLeafValidator} = require("../../utils/queryBuilderLeafValidator");
const {buildExpressionFieldValidator} = require("../../utils/expressionFieldValidator");
const {buildCommandSetOsValidator} = require("../../utils/commandSetOsValidator");
const {buildQueryBuilderColumns} = require("../../utils/queryBuilderColumns");

const Steve = require("@gluenetworks/steve_core").getInstance();
const ruleSevEditor = require("./slickeditors/RuleSeverityEditor");
// const ruleSourceEditor = require('./slickeditors/RuleSourceEditor');
/* global Slick */

const {RULE_UI_ONLY_FIELDS} = require("../../utils/ruleUiOnlyFields");

class AuditPolicyDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("policy-details-view-gl");
        this._rootElem.component = this.model;
        this._logger = this.model.logger;
        this._isDirty = false;
        // true while the JSON pane is shown (vs the form). Owned by the renderer
        // — the component plays no part in edit-mode (matches the rule subview).
        this._jsonMode = false;
        this._formsComponent = new FormsComponent();
        this._fileNamer = FileName;

        this._currentRowId = undefined;
        // One-shot swap flag: set when the outgoing subview was in JSON mode,
        // always consumed and cleared by _onOpenSubview.
        this._reopenSubviewInJsonMode = false;

        this._openNextOrPreviousRule = _.debounce(this._openNextOrPreviousRule.bind(this), 400);
        this._onOpenSubviewDebounced = _.debounce(this._onOpenSubview.bind(this), 1000, {
            leading: true,
            trailing: false,
        });
        this._onEditorResizeDebounced = _.debounce(this._onEditorResize.bind(this), 200);

        // Action order is insertion order within each side (chrome appends in
        // call order). Form-mode left: Add Rule, JSON; right: Download, Save,
        // Cancel. JSON-mode right: Apply (ExitJsonEdit), Cancel (CancelJsonEdit).

        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Download",
            name: "DownloadPolicyDetails",
            handler: (this._onPolicyDetailsDownload = this._onPolicyDetailsDownload.bind(this)),
        });

        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SavePolicyDetails",
            handler: (this._onPolicyDetailsSave = this._onPolicyDetailsSave.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelPolicyDetails",
            handler: (this._onPolicyDetailsCancel = this._onPolicyDetailsCancel.bind(this)),
        });

        // JSON-mode right-side buttons. CancelJsonEdit (Cancel) discards
        // the JSON draft and returns to form mode; ExitJsonEdit (Apply)
        // commits the JSON edits and returns to form mode.
        //
        // Insertion order is Cancel-then-Apply so the visible footer
        // reads "Cancel  Apply" left-to-right on the right side.
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelJsonEdit",
            handler: (this._onCancelJsonEdit = this._onCancelJsonEdit.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Apply",
            name: "ExitJsonEdit",
            handler: (this._onExitJsonEdit = this._onExitJsonEdit.bind(this)),
        });
        // Hidden by default — form mode is the initial state.
        this.model.gameEditor.hideViewAction("ExitJsonEdit");
        this.model.gameEditor.hideViewAction("CancelJsonEdit");

        this._actionsReady = false;

        this._errorShown = false;

        this._renderingForm = false;
        this._toggleActions(false);
        if (this._model._isChildView) {
            this._hideDownloadAction();
        }
        this._registerListeners();

        /* V2 changes */
        this._slickTableContainer = document.createElement("columns-table");
        // this._slickTableContainer.appendChild(this._createNoDataPlaceholder(null, true));
        this._slickColumnDefs = [
            {
                id: "order",
                width: 10,
                name: "",
                //field: "order",
                behavior: "selectAndMove",
                asyncPostRender: function (node) {
                    node[0].setAttribute("title", "Reorder");
                },
                selectable: false,
                resizable: false,
                cssClass: "cell-reorder dnd centered g-icon-drag",
                headerCssClass: "centered",
            },
            {
                id: "enable",
                width: 60,
                name: "Enabled",
                field: "enable",
                toolTip: "enable",
                editor: checkboxEditor,
                formatter: formatters.checkboxFormatter,
            },
            {
                id: "severity",
                width: 150,
                name: "Severity",
                field: "severity",
                toolTip: "Severity",
                formatter: formatters.ruleSeverityFormatter,
                editor: ruleSevEditor,
            },
            {
                id: "name",
                width: 150,
                name: "Name",
                field: "name",
                toolTip: "Name",
                // formatter: formatters.fieldFormatter,
                formatter: formatters.nameFormatter,
                editor: textEditor,
            },
            {
                id: "desc",
                width: 150,
                name: "Description",
                field: "description",
                toolTip: "Description",
                // formatter: formatters.fieldFormatter,
                editor: textEditor,
            },
            {
                id: "source",
                width: 150,
                name: "Source",
                field: "source",
                toolTip: "Source",
                formatter: formatters.ruleSourceFormatter,
            },
            {
                id: "os",
                width: 150,
                name: "OS",
                field: "os",
                toolTip: "OS",
                formatter: formatters.osFormatter,
                // editor: fieldEditor
            },
            {
                id: "commandSet",
                width: 150,
                name: "Command Set",
                field: "commandSet",
                toolTip: "Concept Set",
                formatter: formatters.commandSetFormatter,
            },
            {
                id: "commandResult",
                width: 150,
                name: "Command Result",
                field: "commandResult",
                toolTip: "Concept Result",
                formatter: formatters.commandResultFormatter,
            },
            {
                id: "scope",
                width: 150,
                name: "Query",
                field: "scope",
                toolTip: "Scope",
                formatter: formatters.scopeFormatter,
                // editor: fieldEditor
            },
            {
                id: "indv",
                width: 50,
                name: "Indv",
                field: "scopeSeparateResults",
                toolTip: "Evaluate each result of the filter individually",
                formatter: formatters.checkboxFormatter,
                // editor: fieldEditor
            },
            {
                id: "actionRow",
                width: 80,
                name: "Actions",
                field: "actionRow",
                selectable: false,
                resizable: false,
                cssClass: "cell-delete dnd centered",
                formatter: formatters.actionItems,
                // headerCssClass: 'centered'
            },
        ];

        this._tableOptions = {
            editable: true,
            enableCellNavigation: true,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: false,
            autoEdit: true,
            resizable: true,
            rowHeight: 40,
        };

        this._onSlickClick = _.debounce(this._onSlickClick.bind(this), 500);
        this._onSlickdblClick = this._onSlickdblClick.bind(this);
        this._setRulesOnSlickChanges = this._setRulesOnSlickChanges.bind(this);
        this._onRowCountChange = this._onRowCountChange.bind(this);
        this._onCellChange = this._onCellChange.bind(this);

        this._editorWindow = document.querySelector("lmrb-top-container");

        /* V2 changes End */
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        if (!this._model._policy) {
            this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
            this._model.on(
                "PolicyPropertiesChanged",
                (this._onPolicyPropertiesChanged = this._onPolicyPropertiesChanged.bind(this))
            );
            this._formsComponent.on(
                "PendingPatchesChanged",
                (this._onFormsPendingPatchesChanged = _.debounce(
                    this._onFormsPendingPatchesChanged.bind(this),
                    500,
                    false
                ))
            );
        }
    }

    _unregisterListeners() {
        if (!this._model._policy) {
            this._model.removeListener("Dirty", this._onDirty);
            this._model.removeListener("PolicyPropertiesChanged", this._onPolicyPropertiesChanged);
            this._formsComponent.removeListener(
                "PendingPatchesChanged",
                this._onFormsPendingPatchesChanged
            );
        }
    }

    _registerSlickListeners() {
        this._windowObserver = new ResizeObserver(this._onEditorResizeDebounced);
        this._windowObserver.observe(this._editorWindow);
        this._windowObserver.target = this;
        if (this._slickTable) {
            // console.log(this._slickTable);
            this._slickTable.onClick.subscribe(
                (this._onSlickClick = this._onSlickClick.bind(this))
            );
            this._slickTable.onDblClick.subscribe(this._onSlickdblClick);
            this._slickDataView.onRowCountChanged.subscribe(this._onRowCountChange);
            this._slickDataView.onRowsChanged.subscribe(this._onRowCountChange);
            this._slickTable.onCellChange.subscribe(this._onCellChange);
        }
    }

    _unregisterSlickListeners() {
        if (this._windowObserver) {
            this._windowObserver.disconnect();
        }
        if (this._slickTable) {
            this._slickTable.onClick.unsubscribe(this._onSlickClick);
            this._slickTable.onDblClick.unsubscribe(this._onSlickdblClick);
            this._slickDataView.onRowCountChanged.unsubscribe(this._onRowCountChange);
            this._slickDataView.onRowsChanged.unsubscribe(this._onRowCountChange);
            this._slickTable.onCellChange.unsubscribe(this._onCellChange);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._unregisterSlickListeners();
        if (!this._model._policy) {
            this._onFormsPendingPatchesChanged.flush();
        }
        const promArr = [this._formsComponent.shutdown(), this._removeViewActions()];
        if (this._childViewRenderer) {
            promArr.push(this._childViewRenderer.shutdown());
        }

        if (this._jsonEditor) {
            try {
                this._jsonEditor.destroy();
            } catch (destroyError) {
                this._logger.warn(
                    {error: destroyError},
                    "AuditPolicyDetailsViewRenderer.shutdown() JSON editor destroy failed."
                );
            }
            this._jsonEditor = undefined;
        }

        return Promise.all(promArr).then(
            () => {
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._isDirty = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._errorShown = undefined;
                this._currentRowId = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "AuditPolicyDetailsViewRenderer.shutdown() error.");
            }
        );
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
            this._rootElem.appendChild(this._slickTableContainer);
        }
        return this.model
            .whenModelIsReady()
            .then(() => {
                // append OS values
                this._slickColumnDefs[6].values = this._model._osMap;

                // Add name Id map to commandSet : Start
                const objCommandNameIdMap = Object.values(
                    this._model._commandSetFeed.getCache()
                ).reduce((acc, i) => {
                    Object.assign(acc, {[i.getId()]: i.getName()});
                    return acc;
                }, {});
                this._slickColumnDefs.find((i) => i.id === "commandSet")["nameIdMap"] =
                    objCommandNameIdMap;
                // Add name Id map to commandSet : End

                // Concept Id and RSI Id name Id map to commandResult : Start
                const conceptItemNameIdMap = Object.values(
                    this._model._gameEditor._conceptItems
                ).reduce((acc, i) => {
                    Object.assign(acc, {[i.value]: i.label});
                    return acc;
                }, {});
                const rsiIdsNameIdMap = Object.values(this._model._gameEditor._rsiIds).reduce(
                    (acc, i) => {
                        Object.assign(acc, {[i.value]: i.label});
                        return acc;
                    },
                    {}
                );
                this._slickColumnDefs.find((i) => i.id === "commandResult")[
                    "conceptItemNameIdMap"
                ] = conceptItemNameIdMap;
                this._slickColumnDefs.find((i) => i.id === "commandResult")["rsiIdNameIdMap"] =
                    rsiIdsNameIdMap;
                // Concept Id and RSI Id name Id map to commandResult : End

                const jtype = this._getJtype();
                const pristineValue = this._prepareValues();

                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);
                this._renderingForm = true;

                return this._formsComponent.renderer.render();
            })
            .then(() => {
                this._renderingForm = false;
                // const rules = this._model._auditPolicy.getRules();
                // console.log(rules);
                if (this._model._isReadOnly) {
                    this._tableOptions.editable = false;
                    this._rootElem.classList.add("read-only");
                }
                this._slickDataView = new Slick.Data.DataView();
                this._slickData = this._mapRulesForSlick(this._model._auditPolicy);
                this._slickDataView.setItems(this._slickData);
                this._slickTable = new Slick.Grid(
                    this._slickTableContainer,
                    this._slickDataView,
                    this._slickColumnDefs,
                    this._tableOptions
                );
                this._slickTableContainer.appendChild(this._createNoDataPlaceholder());
                this._toggleNoDataPlaceholder(!this._slickDataView.getItems().length);
                // console.log(this._slickTable);
                this._slickTable.setSelectionModel(new Slick.RowSelectionModel());
                if (!this._model._isReadOnly) {
                    this._registerRowMovePlugin(this._slickTable, this._slickDataView);
                }

                this._registerSlickListeners();

                if (this._model._commitHash && this._model._auditPolicy.isDirty()) {
                    this._model._auditPolicy.emit("DirtyStateChanged", true);
                }

                return Promise.resolve();
            })
            .then(
                () => {
                    if (!this._actionsReady) {
                        this._actionsReady = true;
                        this.model.gameEditor.addViewAction({
                            type: "normal",
                            displayName: "Add Rule",
                            name: "addRule",
                            position: "left",
                            handler: (this._onAddRule = this._onAddRule.bind(this)),
                        });
                        this.model.gameEditor.addViewAction({
                            type: "normal",
                            displayName: "JSON",
                            name: "EnterJsonEdit",
                            position: "left",
                            handler: (this._onEnterJsonEdit = this._onEnterJsonEdit.bind(this)),
                        });
                        if (this._model._isReadOnly) {
                            this.model.gameEditor.disableViewAction("addRule");
                        }
                    }
                    this._toggleActions(false);
                    return this._renderPolicyDetails();
                },
                (err) => err
            );
    }

    updateSlickGrid() {
        this._slickData = this._mapRulesForSlick(this._model._auditPolicy);
        this._slickDataView.setItems(this._slickData);
        this._slickTable.invalidateAllRows();
        this._slickTable.resizeCanvas();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _removeViewActions() {
        this.model.gameEditor.removeViewAction("SavePolicyDetails");
        this.model.gameEditor.removeViewAction("CancelPolicyDetails");
        this.model.gameEditor.removeViewAction("DownloadPolicyDetails");
        this.model.gameEditor.removeViewAction("addRule");
        this.model.gameEditor.removeViewAction("EnterJsonEdit");
        this.model.gameEditor.removeViewAction("ExitJsonEdit");
        this.model.gameEditor.removeViewAction("CancelJsonEdit");
    }

    /*******************************************/
    /* JSON EDIT MODE                          */
    /*******************************************/

    _ensureJsonContainer() {
        if (this._jsonContainer) {
            return this._jsonContainer;
        }
        this._jsonContainer = document.createElement("div");
        // Sizing + visibility are driven by .audit-policy-json-pane and the
        // .json-edit-mode toggle on _rootElem — see _policy_details.layout.scss.
        this._jsonContainer.classList.add("audit-policy-json-pane", "hidden");
        this._rootElem.appendChild(this._jsonContainer);
        return this._jsonContainer;
    }

    _serializePolicyForJson() {
        // Build a labelized projection of the policy that contains only the
        // user-editable top-level fields. Anything else (read-only audit
        // metadata, server-managed bookkeeping) is intentionally omitted.
        //
        // Note: `_mapRulesForSlick` mutates the entity's rule objects in
        // place to stamp on the severity / source / OS lookup maps + dirty
        // flag for in-grid rendering — so even policy.getRules() returns
        // polluted objects. Strip those UI-only fields before labelizing.
        const auditPolicy = this.model.auditPolicy;
        const labelMaps = this.model.getRuleLabelMaps();
        const policyProjection = {
            name: auditPolicy.name || "",
            description: auditPolicy.description || "",
            permissionLevel: auditPolicy.permissionLevel || "",
            rules: [],
        };
        const clonedRules = _.cloneDeep(auditPolicy.getRules() || []);
        clonedRules.forEach((rule) => {
            const sanitizedRule = _.omit(rule, RULE_UI_ONLY_FIELDS);
            const {rule: labelizedRule} = ruleUuidsToLabels(sanitizedRule, labelMaps);
            policyProjection.rules.push(labelizedRule);
        });
        return policyProjection;
    }

    _onEnterJsonEdit() {
        if (this._jsonMode) {
            return;
        }
        this._jsonMode = true;
        this._enterJsonMode();
    }

    _onExitJsonEdit() {
        // Accept path: _exitJsonMode parses the JSON and pushes it into the entity.
        if (!this._jsonMode) {
            return;
        }
        this._jsonMode = false;
        this._exitJsonMode();
    }

    _onCancelJsonEdit() {
        // Cancel path: _jsonCancelling short-circuits _exitJsonMode so it skips
        // the parse + applyParsedPolicy steps (the flag is cleared there).
        // A dirty draft gets a confirmation veil first; "No" stays in the
        // JSON pane with the draft intact.
        if (!this._jsonMode) {
            return;
        }
        const discardDraft = () => {
            this._jsonCancelling = true;
            this._jsonMode = false;
            this._exitJsonMode();
        };
        if (!this._jsonHasDraft) {
            discardDraft();
            return;
        }
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            discardDraft
        );
    }

    // Toggle the Accept (ExitJsonEdit) button based on whether the user
    // has typed anything yet and the JSON currently validates cleanly.
    // Called from the editor's onUserChangedContent and onValidationChange
    // callbacks below.
    _refreshJsonAcceptEnabled() {
        if (!this.model || !this.model.gameEditor) {
            return;
        }
        const hasErrors = (this._jsonValidationErrors || []).length > 0;
        if (this._jsonHasDraft && !hasErrors) {
            this.model.gameEditor.enableViewAction("ExitJsonEdit");
        } else {
            this.model.gameEditor.disableViewAction("ExitJsonEdit");
        }
    }

    // Navigate-away guard for an unapplied JSON draft.
    _refreshJsonLeaveGuard() {
        const gameEditor = this.model && this.model.gameEditor;
        if (!gameEditor) {
            return;
        }
        if (this._jsonMode && this._jsonHasDraft) {
            if (!this._confirmDiscardOnLeaveBound) {
                this._confirmDiscardOnLeaveBound = this._confirmDiscardOnLeave.bind(this);
            }
            gameEditor.setOnBeforeUnload(this._confirmDiscardOnLeaveBound);
        } else {
            this.model._onDirtyStateChanged(this.model._auditPolicy.isDirty());
        }
    }

    // onBeforeUnload handler for an unapplied JSON draft (resolve → leave,
    // reject → stay). Mirrors the standard entity veil's Save/Discard
    // semantics with JSON-aware wording:
    //   - Apply & Save: apply the JSON draft to the entity, then save the
    //     policy and proceed with the close. If the JSON is invalid, abort
    //     the close instead — the user stays in the JSON pane with the
    //     gutter errors (never apply/save invalid JSON, never drop the draft).
    //   - Discard: drop the JSON draft AND the entity's pending changes,
    //     then proceed with the close (standard rejection() behavior).
    _confirmDiscardOnLeave() {
        const entityDirty = this.model._auditPolicy.isDirty();
        const message = entityDirty
            ? "You have unsaved changes in both the form and the JSON editor. Saving will apply your JSON changes and then save the policy."
            : "You have unsaved changes in the JSON editor. Saving will apply your JSON changes and then save the policy.";
        return this.model._modalUserFeedback
            .displayConfirmation(message, {
                title: "Unsaved Changes",
                confirmDetails: {
                    text: "Apply & Save",
                    type: "additive",
                },
                rejectDetails: {
                    text: "Discard",
                    type: "text",
                },
            })
            .then(() => {
                if ((this._jsonValidationErrors || []).length) {
                    return this.model._modalUserFeedback.hideDisplayed().then(() => {
                        throw {jsonInvalid: true};
                    });
                }
                this._jsonMode = false;
                this._exitJsonMode();
                this.model.gameEditor.clearOnBeforeUnload();
                if (this.model.isPolicyFormValid(true)) {
                    return this.model.savePolicy(false).then(() => undefined);
                }
                return undefined;
            })
            .catch((err) => {
                if (!err || !err.userRejected) {
                    // jsonInvalid abort (or unexpected error) — keep the
                    // rejection so navigation stays.
                    throw err;
                }
                // Discard: drop the draft + entity pending changes, proceed.
                this._jsonCancelling = true;
                this._jsonMode = false;
                this._exitJsonMode();
                this._resetJsonDraftState();
                return Promise.resolve(this.model._auditPolicy.clearPendingPatches(true))
                    .then(() => this.model._modalUserFeedback.hideDisplayed())
                    .then(() => this.model._gameEditor.getTabCmpnt().setDirty(false))
                    .then(() => {
                        this.model.gameEditor.clearOnBeforeUnload();
                        return undefined;
                    });
            });
    }

    // Toggle `.json-edit-mode` on the host — form-side siblings are hidden
    // via CSS (see `_policy_details.layout.scss`).
    _setFormPaneVisible(visible) {
        if (!this._rootElem) {
            return;
        }
        this._rootElem.classList.toggle("json-edit-mode", !visible);
    }

    _enterJsonMode() {
        this._ensureJsonContainer();

        // Flush in-flight form/slick state into the policy entity before we
        // serialize. Without this a user who types in a form field or edits
        // a SlickGrid cell and immediately clicks JSON Edit would see stale
        // JSON: _onFormsPendingPatchesChanged is debounced 500ms, and the
        // currently-active SlickGrid cell editor still has its draft value.
        if (this._onFormsPendingPatchesChanged && this._onFormsPendingPatchesChanged.flush) {
            try {
                this._onFormsPendingPatchesChanged.flush();
            } catch (flushError) {
                this._logger.warn(
                    {error: flushError},
                    "AuditPolicyDetailsViewRenderer._enterJsonMode() failed to flush pending form patches."
                );
            }
        }
        if (this._slickTable && this._slickTable.getEditController) {
            try {
                this._slickTable.getEditController().commitCurrentEdit();
            } catch (commitError) {
                this._logger.warn(
                    {error: commitError},
                    "AuditPolicyDetailsViewRenderer._enterJsonMode() failed to commit active slick cell edit."
                );
            }
        }

        const policyProjection = this._serializePolicyForJson();
        // Build runtime-tightened schemas (label enums for permissionLevel /
        // os / commandSet / conceptItemId / rsiId sourced from the
        // currently-loaded policy) and a post-validator for enum-typed
        // custom-field option values inside expressions.
        const labelMaps = this.model.getRuleLabelMaps();
        const qbColumns = buildQueryBuilderColumns();
        const dynamicSchemas = buildDynamicSchemas({
            labelMaps,
            osMap: this.model._osMap || {},
            permLevels: this.model._permLevels || [],
            qbColumns,
        });
        const schemaEntries = buildPolicySchemaEntries(policyProjection, dynamicSchemas);
        const customFieldEnumValidator = buildCustomFieldEnumValidator(labelMaps);
        const queryBuilderLeafValidator = buildQueryBuilderLeafValidator(qbColumns);
        // Device-column set for ifExpression field-name validation (base
        // columns + nodeProps, loaded in whenModelIsReady). Empty if the load
        // failed, in which case the validator no-ops rather than false-flags.
        const expressionFieldValidator = buildExpressionFieldValidator(
            this.model.getExpressionFieldNames()
        );
        const commandSetOsValidator = buildCommandSetOsValidator(labelMaps);
        const extraValidators = [
            customFieldEnumValidator,
            queryBuilderLeafValidator,
            expressionFieldValidator,
            commandSetOsValidator,
        ];

        this._resetJsonDraftState();
        this._jsonInitialContent = _.cloneDeep(policyProjection);

        const onUserChangedContent = ({newValue}) => {
            // newValue is parsed when JSON is valid, raw text (string) when
            // not. Only "reverted" when a parseable value matches the original.
            const reverted = !_.isString(newValue) && _.isEqual(newValue, this._jsonInitialContent);
            this._jsonHasDraft = !reverted;
            this._refreshJsonAcceptEnabled();
            this._refreshJsonLeaveGuard();
        };
        const onValidationChange = ({errors}) => {
            this._jsonValidationErrors = errors || [];
            this._refreshJsonAcceptEnabled();
        };

        if (!this._jsonEditor) {
            this._jsonEditor = JsonEditor.create({
                container: this._jsonContainer,
                schemas: schemaEntries,
                extraValidators,
                initialContent: policyProjection,
                onUserChangedContent,
                onValidationChange,
            });
        } else {
            // Install schemas/validators silently — setContent below triggers
            // the single validation pass for this entry.
            this._jsonEditor.setSchemas(schemaEntries, {skipValidation: true});
            this._jsonEditor.setExtraValidators(extraValidators, {skipValidation: true});
            this._jsonEditor.setContent(policyProjection);
        }

        // Hide every form-side sibling under _rootElem; show the JSON pane.
        this._setFormPaneVisible(false);
        this._jsonContainer.classList.remove("hidden");
        // Show JSON-mode footer; Save/Cancel/back are governed by
        // _showViewActions and hide once _toggleActions re-evaluates.
        this.model.gameEditor.hideViewAction("addRule");
        this.model.gameEditor.hideViewAction("EnterJsonEdit");
        this.model.gameEditor.showViewAction("ExitJsonEdit");
        this.model.gameEditor.showViewAction("CancelJsonEdit");
        this._refreshJsonAcceptEnabled();
        this._toggleActions(true);
        this._jsonEditor.resize();
        this._jsonEditor.focus();
    }

    _exitJsonMode() {
        // _onCancelJsonEdit sets _jsonCancelling before the mode-change to
        // distinguish Accept (parse+apply) from Cancel (discard draft).
        const cancelling = !!this._jsonCancelling;
        this._jsonCancelling = false;

        if (!cancelling && this._jsonEditor) {
            // Best-effort: parse + push valid sub-trees into the entity.
            // Invalid sub-trees stay in the editor buffer so re-toggling JSON
            // restores the user's text.
            const {parsed: parsedPolicy} = this._jsonEditor.runValidation();
            if (parsedPolicy && this.model.applyParsedPolicy) {
                try {
                    // Live validators (schema enums + custom-field validator)
                    // already disable Accept for unrecognized names, so any
                    // resolution error here means the label maps were empty —
                    // log for diagnostics rather than block (blocking would
                    // false-positive on every name).
                    const labelErrors = this.model.applyParsedPolicy(parsedPolicy);
                    if (labelErrors && labelErrors.length) {
                        this._logger.warn(
                            {errors: labelErrors},
                            "AuditPolicyDetailsViewRenderer._exitJsonMode() unresolved label references."
                        );
                    }
                } catch (applyError) {
                    this._logger.warn(
                        {error: applyError},
                        "AuditPolicyDetailsViewRenderer._exitJsonMode() apply error."
                    );
                }
            }
        }

        if (this._jsonContainer) {
            this._jsonContainer.classList.add("hidden");
        }
        this._setFormPaneVisible(true);
        // Re-seed the forms component from the entity — the form is only
        // wired form→entity, and the back-sync is gated on `!_isDirty`.
        // Mirrors the initial render (setPristineValue + explicit renderer.render),
        // since PristineValueChanged alone doesn't reliably refresh enum widgets
        // like the permissionLevel dropdown. `_renderingForm` guards the
        // resulting PendingPatchesChanged from looping back through
        // `_onFormsPendingPatchesChanged`.
        if (this._formsComponent) {
            this._renderingForm = true;
            try {
                this._formsComponent.setPristineValue(this._prepareValues(), true);
                Promise.resolve(this._formsComponent.renderer.render()).finally(() => {
                    this._renderingForm = false;
                });
            } catch (refreshError) {
                this._renderingForm = false;
                this._logger.warn(
                    {error: refreshError},
                    "AuditPolicyDetailsViewRenderer._exitJsonMode() failed to refresh forms component."
                );
            }
        }
        // Refresh the slickgrid in case rules changed via JSON.
        if (this._slickDataView) {
            this.updateSlickGrid();
        }
        if (!this.model._isReadOnly) {
            this.model.gameEditor.showViewAction("addRule");
        }
        this.model.gameEditor.hideViewAction("ExitJsonEdit");
        this.model.gameEditor.hideViewAction("CancelJsonEdit");
        this.model.gameEditor.showViewAction("EnterJsonEdit");
        // Save/Cancel + child-view back button are restored by the standard
        // _toggleActions → _showViewActions flow now that _jsonMode is false.
        this._toggleActions(true);
        // Draft is gone — drop the JSON leave guard and restore the standard
        // entity-dirty onBeforeUnload arming.
        this._refreshJsonLeaveGuard();
    }

    _showViewActions(fromChild = true) {
        // While the JSON pane is up, Save / Cancel / BackButton act on a
        // form state the user can't see — hide them regardless of dirty
        // state. Form mode keeps the legacy behavior: show only when dirty.
        const isJsonMode = this._jsonMode;
        if (isJsonMode) {
            this.model.gameEditor.hideViewAction("SavePolicyDetails");
            this.model.gameEditor.hideViewAction("CancelPolicyDetails");
            if (this.model._isChildView && fromChild) {
                this.model._gameEditor.hideViewAction("BackButtonForChildView");
            }
            return;
        }
        if (this.model._auditPolicy.isDirty && this.model._auditPolicy.isDirty()) {
            this.model.gameEditor.showViewAction("SavePolicyDetails");
            this.model.gameEditor.showViewAction("CancelPolicyDetails");
        }
        if (this.model._isChildView && fromChild) {
            this.model._gameEditor.showViewAction("BackButtonForChildView");
        }
    }

    _hideViewActions(fromChild = true) {
        this.model.gameEditor.hideViewAction("SavePolicyDetails");
        this.model.gameEditor.hideViewAction("CancelPolicyDetails");
        if (this.model._isChildView && fromChild) {
            this.model.emit("HideBackButton");
        }
    }

    _disableViewActions() {
        this.model.gameEditor.disableViewAction("SavePolicyDetails");
        this.model.gameEditor.disableViewAction("CancelPolicyDetails");
    }

    _enableViewActions() {
        this.model.gameEditor.enableViewAction("SavePolicyDetails");
        this.model.gameEditor.enableViewAction("CancelPolicyDetails");
    }
    _showDownloadAction() {
        // Download is form-mode chrome; hide it while the JSON pane is up.
        // _toggleActions re-evaluates on every dirty-state change, so the
        // JSON-mode override has to live here, not just at JSON-mode entry.
        if (this._model._isChildView) {
            return;
        }
        if (this._jsonMode) {
            this.model.gameEditor.hideViewAction("DownloadPolicyDetails");
            return;
        }
        this.model.gameEditor.showViewAction("DownloadPolicyDetails");
    }

    _hideDownloadAction() {
        this.model.gameEditor.hideViewAction("DownloadPolicyDetails");
    }

    _toggleActions(fromChild) {
        if (this._isDirty) {
            this._showViewActions(fromChild);
            this._hideDownloadAction();
        } else {
            this._hideViewActions(fromChild);
            if (
                !_.isUndefined(this.model.auditPolicy) &&
                !_.isUndefined(this.model.auditPolicy.updatedOn) &&
                !_.isUndefined(this.model.auditPolicy.rules)
            ) {
                this._showDownloadAction(fromChild);
            } else {
                this._hideDownloadAction();
            }
        }
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderPolicyDetails() {
        return Promise.resolve()
            .then(() => {
                // const data = this._slickDataView.getItems();

                // data.forEach(currentRow => {
                //     if(currentRow.conceptItem !== '' && currentRow.conceptItemId === null) {
                //         if(currentRow.conceptItem) {
                //             const currentConceptId = Object.values(this._model._gameEditor.conceptItems).find(i => i.label === currentRow.conceptItem).value;
                //             currentRow.conceptItemId = currentConceptId;
                //         }
                //     }
                // });
                // this._slickDataView.setItems(data);

                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "AuditPolicyDetailsViewRenderer._renderPolicyDetails() error."
                );
            });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            const form = this._formsComponent.getValue(),
                formName = form.name;

            this.model.auditPolicy.setName(formName);
            this.model.auditPolicy.setPermissionLevel(form.permissionLevel);
            this.model.auditPolicy.setDescription(form.description);
        }
        return Promise.resolve();
    }

    _disableConceptsDropDown() {
        const conceptItems = this._formsComponent._renderer
            .getRootElement()
            .querySelectorAll(".concept_item_id");
        if (conceptItems.length > 0) {
            conceptItems.forEach((concepts) => {
                const select = concepts.querySelector("select");
                if (select.options.length < 2) {
                    concepts.classList.add("disabled");
                    concepts.querySelector(".chosen-single > span").innerText =
                        "No Concept Items available";
                }
            });
        }
    }

    _onPolicyPropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            const auditPolicyPropsVal = this.model.auditPolicy.getPristineProperties();

            if (!_.isUndefined(auditPolicyPropsVal.rules)) {
                auditPolicyPropsVal.rules.forEach((rule) => {
                    if (rule.os === "") {
                        rule.os = "all";
                    }
                });
            }

            // JSON drafts don't reach the entity until Accept, so include
            // them in the dirty check.
            const isDirty = this._isDirty || (this._jsonMode && this._jsonHasDraft === true);

            const updatedOn = this.model.auditPolicy.updatedOn;
            if (updatedOn > (this._model._lastCommitUpdatedOn || 0)) {
                auditPolicyPropsVal.lastCommitStatus = "PENDING";
            } else {
                auditPolicyPropsVal.lastCommitStatus = auditPolicyPropsVal.lastCommitHash;
            }

            if (isDirty) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    if (
                        !this._model.readOnlyFieldIds.includes(id) &&
                        !_.isEqual(
                            _.isEmpty(pristineProperties[id]) ? "" : pristineProperties[id],
                            _.isEmpty(before[id]) ? "" : before[id]
                        )
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });
                if (!isReadOnlyUpdate) {
                    this.model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This audit policy has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onPolicyDetailsCancelAction =
                            this._onPolicyDetailsCancelAction.bind(this))
                    );
                }
            } else {
                this._formsComponent.setPristineValue(auditPolicyPropsVal);
                this.updateSlickGrid();
            }
        }
    }

    _onDirty(dirtyState) {
        this._isDirty = dirtyState;
        this._toggleActions(true);
    }

    // Shared with the JSON edit pane via ruleUuidsToLabels. Mutates `rule` in
    // place — and drops the four UUID slots unconditionally so legacy export
    // output never carries a stray UUID alongside its label.
    _processRuleForUUID(rule, rsiIdsMap, conceptItems, commandSetValToLabelMap) {
        const labelMaps = {
            conceptItems,
            rsiIdsMap,
            commandSets: commandSetValToLabelMap,
            customFields: this.model._customFieldsWithInherited || {},
            tags: this.model._tagIdToNameMap || {},
        };
        const {rule: labelizedRule} = ruleUuidsToLabels(rule, labelMaps);
        delete rule.commandSet;
        delete rule.conceptItemId;
        delete rule.rsiId;
        if (rule.commandSetResult) {
            delete rule.commandSetResult.conceptItemId;
            delete rule.commandSetResult.restSyncItemId;
        }
        Object.assign(rule, labelizedRule);
        return rule;
    }

    _onPolicyDetailsDownload() {
        this.model.gameEditor.disableViewAction("DownloadPolicyDetails");
        const policy = this.model.auditPolicy;
        //const fileName = "DeviceAudit-" + policy.name + "-" + new Date(policy.updatedOn).toISOString();
        const fileObject = {};
        const conceptItems = this.model.gameEditor.conceptItems || {};
        const rsiIdsMap = this._model._gameEditor._rsiIdsMap || {};
        const cmdSetValLabelMap = this._model.cmdSetValLabelMap || {all: "All"};

        fileObject.name = policy.name;
        fileObject.description = policy.description;
        fileObject.permissionLevel = policy.permissionLevel;
        fileObject.rules = _.clone(policy.rules);

        fileObject.rules.forEach((objRule) => {
            objRule.required = objRule.expected;
            delete objRule.expected;
            this._processRuleForUUID(objRule, rsiIdsMap, conceptItems, cmdSetValLabelMap);
        });

        const fileName = this._fileNamer({
            solution: "CDA",
            general: "PolicyDetails",
            specific: policy.name,
            date: new Date(policy.updatedOn),
            suffix: "json",
        });
        this.model.gameEditor.saveJsonFile(fileName, JSON.stringify(fileObject));
        this.model.gameEditor.enableViewAction("DownloadPolicyDetails");
        return this;
    }

    _onOpenSubview(row = false, newRule = false) {
        // Consume the one-shot swap flag up front so grid double-click, the
        // row edit action, and Add Rule always open in form mode.
        const startInJsonMode = this._reopenSubviewInJsonMode === true;
        this._reopenSubviewInJsonMode = false;

        const allItems = this._slickTable.getData().getItems(),
            allItemIdList = allItems.map((i) => i.id),
            currentItemIndex = allItemIdList.indexOf(row.id) + 1;

        // Blocks a second subview opening mid-render, since addViewAction
        // has no dedup-by-name. Defers to an already-shown veil (e.g. from
        // _prevClick/_nextClick) instead of double-showing it.
        const manageOpeningVeil = !this._model._modalUserFeedback.isDisplayed();
        if (manageOpeningVeil) {
            this._model._modalUserFeedback.displayLoading();
        }

        this._editView = new editPolicyView({
            parentView: this._model,
            currentRow: row,
            newRule,
            startInJsonMode,
        });

        return Promise.resolve()
            .then(() => {
                return this._editView.renderer.render();
            })
            .then(() => {
                this._childViewRenderer = new childViewComponent({
                    data: this._editView.renderer.rootElement,
                    backLink: "Details",
                    title: "Rule Details",
                    childData: this._editView,
                    parentData: this._model,
                    backButton: false,
                    backCallback: () => this._editView._renderer._onGoBackFromSubview(),
                });

                this._currentRowId = row.id;
                this._count = document.createElement("span");
                this._count.innerHTML = `Rule ${currentItemIndex} of ${allItemIdList.length}`;
                this.prev = document.createElement("span");
                this.prev.classList.add("prev-btn", "g-icon", "g-icon-chevron-down");
                // this.prev.innerHTML = "< Previous";
                this.next = document.createElement("span");
                this.next.classList.add("next-btn", "g-icon", "g-icon-chevron-down");
                // this.next.innerHTML = "Next >";
                const rightContainer = this._childViewRenderer._titleCmpnt.querySelector(
                    "game-editor-header-right-content"
                );

                this.prev.addEventListener("click", (this._prevClick = this._prevClick.bind(this)));
                this.next.addEventListener("click", (this._nextClick = this._nextClick.bind(this)));

                rightContainer.appendChild(this._count);
                rightContainer.appendChild(this.prev);
                rightContainer.appendChild(this.next);

                this._enableDisableNextPrev(row.id);
                this._rootElem.classList.remove("new-rule");

                return Promise.resolve();
            })
            .finally(() => {
                if (manageOpeningVeil) {
                    this._model._modalUserFeedback.hideDisplayed();
                }
            });
    }

    async _prevClick(e) {
        if (e.target.classList.contains("disabled-btn")) {
            return;
        }

        this._model._modalUserFeedback.displayLoading();

        this._openNextOrPreviousRule(0);
    }
    _nextClick(e) {
        if (e.target.classList.contains("disabled-btn")) {
            return;
        }

        this._model._modalUserFeedback.displayLoading();

        this._openNextOrPreviousRule(1);
    }

    _openNextOrPreviousRule(mode) {
        return this._editView._renderer._prepareRuleSwap().then(({proceed, resumeJson}) => {
            if (!proceed) {
                // Apply aborted or the user kept their JSON draft — clear the
                // chevron click's loading veil and stay on the current rule.
                this._model._modalUserFeedback.hideDisplayed();
                return Promise.resolve();
            }
            // A confirmation workflow hides every veil on its way out;
            // re-cover the swap.
            this._model._modalUserFeedback.displayLoading();
            const allItems = this._slickTable.getData().getItems(),
                allItemIdList = allItems.map((i) => i.id),
                currentItemIndex = allItemIdList.indexOf(this._currentRowId),
                nextDisplayedItem =
                    mode === 1 ? allItems[currentItemIndex + 1] : allItems[currentItemIndex - 1];

            this._reopenSubviewInJsonMode = resumeJson;

            return this._onOpenSubview(nextDisplayedItem).then(() => {
                this._model._modalUserFeedback.hideDisplayed();
            });
        });
    }

    _enableDisableNextPrev(rowId) {
        const slickOrder = this._slickTable
            .getData()
            .getItems()
            .map((i) => i.id);

        this.prev.classList.remove("disabled-btn");
        this.next.classList.remove("disabled-btn");
        if (slickOrder.indexOf(rowId) === 0) {
            this.prev.classList.add("disabled-btn");
        }
        if (slickOrder.indexOf(rowId) === slickOrder.length - 1) {
            this.next.classList.add("disabled-btn");
        }
    }

    _onPolicyDetailsSave() {
        this._disableViewActions();
        this._model._modalUserFeedback.displayLoading();

        // const rules = this._formsComponent.getValue().rules,
        // errors = this.model.auditPolicy.validateRules(rules);
        // errors = [];
        // if (errors.length > 0) {
        //     // TODO To be refactored into a unique isPolicyFormValid() method
        //     let errorMsg = "";
        //     errors.forEach((error) => {
        //         let ruleName = "'" + (_.get(error, ["rule", "name"], "").trim() || "unknown") + "' ";
        //         if (error.msg === "name is required") {
        //             errorMsg = errorMsg + "Rule name is required\n\n";
        //         } else if (error.msg === "expected or forbidden array must contain at least one rule") {
        //             errorMsg = errorMsg + "Rule " + ruleName + "must contain at least one required or forbidden value\n\n";
        //         } else {
        //             errorMsg = errorMsg + "Rule " + ruleName + error.msg + "\n\n";
        //         }
        //     });
        //     this.model._logger.warn(errors, "ConfigAuditPolicy.setRules() - Error");
        //     this.model._modalUserFeedback.displayError({ error: { errorMsg } });
        //     this._enableViewActions();
        //     this._disableConceptsDropDown();
        // } else {
        setTimeout(() => {
            this.model
                .savePolicy()
                .then(() => {
                    this._enableViewActions();
                })
                .catch((/*err*/) => {
                    this._enableViewActions();
                })
                .finally(() => {
                    this._model._modalUserFeedback.hideDisplayed();
                });
        }, 100);
        // }
    }

    _onPolicyDetailsCancel() {
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onPolicyDetailsCancelAction = this._onPolicyDetailsCancelAction.bind(this))
        );
    }

    _onPolicyDetailsCancelAction() {
        this.model.resetPolicyProperties();
        // Remote-update veil's "discard my changes" path: drop any JSON draft
        // and fall back to form mode via the cancel path (no parse/apply).
        // The next _enterJsonMode call re-seeds the editor from the fresh entity.
        if (this._jsonMode) {
            this._jsonCancelling = true;
            this._jsonMode = false;
            this._exitJsonMode();
            this._resetJsonDraftState();
        }
        return this.render();
    }

    _resetJsonDraftState() {
        this._jsonHasDraft = false;
        this._jsonValidationErrors = [];
        this._jsonInitialContent = undefined;
    }

    _onSlickdblClick(e, args) {
        // console.log("called", e, args);
        const {row, cell, grid} = args;
        const cellId = grid.getColumns()[cell].id;
        if (
            cellId === "conceptItem" ||
            cellId === "source" ||
            cellId === "os" ||
            cellId === "scope" ||
            cellId === "indv"
        ) {
            this._onOpenSubviewDebounced(grid.getData().getItem(row));
        }
    }

    _onSlickClick(e, args) {
        const {row, cell, grid} = args;
        const cellId = grid.getColumns()[cell].id;
        if (cellId === "actionRow") {
            let actionEl = e.target;
            let action;
            while (actionEl.parentElement) {
                if (actionEl.tagName.toLowerCase() === "span") {
                    action = actionEl.className;
                    break;
                }
                actionEl = actionEl.parentElement;
            }

            if (!action) {
                return;
            }
            const dataView = grid.getData();
            const rules = dataView.getItems();
            let currRule = rules[row];
            switch (action) {
                case "clone":
                    if (this._model._isReadOnly) {
                        break;
                    }
                    setTimeout(() => {
                        currRule = _.cloneDeep(currRule);
                        currRule.dirty = true;
                        currRule.id =
                            (row + 1) * new Date().getTime() + Math.random().toString(36).substr(2);
                        rules.splice(row + 1, 0, currRule); // Does inplace edit - Mutates
                        dataView.setItems(rules);
                        this._refreshSlickGrid();
                        this._editSlickCell(row + 1, 2); // 2, cell index for name
                    }, 10);
                    break;

                case "delete":
                    if (this._model._isReadOnly) {
                        break;
                    }
                    rules.splice(row, 1); // Does inplace edit - Mutates
                    dataView.setItems(rules);
                    this._refreshSlickGrid();
                    break;
                case "edit":
                    // newRule arg is true if id is not an integer as it's a cloned rule with a unique timestamped id
                    // cloned rules are always new but don't necessarily have a pending patch
                    this._onOpenSubviewDebounced(
                        grid.getData().getItem(row),
                        _.isInteger(currRule.id) ? false : true
                    );
                    break;
            }
        }
    }
    _hideLeftFooterContainer() {}

    _showLeftFooterContainer() {
        // this._currentRowId = undefined;
    }

    _refreshGrid() {}

    _onEditorResize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
        }
    }

    _onAddRule() {
        this._rootElem.classList.add("new-rule");

        const rule = {};
        rule.severity = "INFO";
        rule.name = "New rule";

        const lastSelectedOS = this._model._userDataStorage.getData("selectedOs", true);
        if (lastSelectedOS) {
            const lastSelectedOSExists =
                Object.keys(this._model._osMap).indexOf(lastSelectedOS) > -1 ? true : false;
            if (lastSelectedOSExists) {
                rule.os = lastSelectedOS;
            } else {
                rule.os = "all";
            }
        }

        rule.source = "LATEST";
        rule.commandSet = "all";
        rule.commandSetResult = {
            type: "basicMode",
        };
        rule.type = "SIMPLE";
        rule.id = this._slickDataView.getItems().length;
        rule.sevMap = this._model._auditPolicy.getRuleSeverityMap();
        rule.sourceMap = this._model._auditPolicy.getRuleSourceMap();
        rule.osMap = this._model._gameEditor.os;
        rule.dirty = true;
        this._slickDataView.addItem(rule);
        // this._editSlickCell(this._slickDataView.getItems().length - 1, 2); // 2, cell index for name
        this._onOpenSubviewDebounced(
            this._slickDataView.getItems()[this._slickDataView.getItems().length - 1],
            true
        );
        this._refreshSlickGrid();
    }

    _onRowCountChange(e, {/*previous,*/ current /*, dataView, callingOnRowsChanged*/}) {
        this._setRulesOnSlickChanges(this._slickTable);
        if (current !== undefined) {
            this._toggleNoDataPlaceholder(!current);
        }
    }

    _onCellChange(e, args) {
        _.set(args.item, "dirty", true);
        this._setRulesOnSlickChanges(args.grid);
        this._refreshSlickGrid();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _setRulesOnSlickChanges(grid) {
        if (!this._model._policy) {
            const rules = this._generateRules(grid.getData().getItems());
            this._model._auditPolicy.setRules(rules);
        }
    }

    _generateRules(data) {
        return data.map((rule) => {
            const updatedRule = _.omit(rule, RULE_UI_ONLY_FIELDS);
            if (updatedRule.os === "Any") {
                updatedRule.os = "";
            }
            return updatedRule;
        });
    }
    _refreshSlickGrid() {
        this._slickDataView.refresh();
        this._slickTable.invalidateAllRows();
        this._slickTable.resizeCanvas();
    }
    _editSlickCell(row, cell) {
        _.delay(() => {
            this._slickTable.scrollRowToTop(row);
            this._slickTable.setActiveCell(row, cell); // 2, cell index for name
            this._slickTable.editActiveCell();
        }, 100);
    }
    _mapRulesForSlick(policy) {
        const hasPatchWithCurrentRule = (rule, id) => {
            const policy = this._model?._auditPolicy;
            if (!policy || typeof policy.getPendingPatches !== "function") {
                return false;
            }
            const pendingPatches = policy.getPendingPatches();
            if (!pendingPatches) {
                return false;
            }
            // Match "/rules/<id>" as a complete JSON-pointer segment. A bare
            // substring check matches "/rules/1" inside "/rules/11/...", which
            // wrongly lights up rule 1 whenever any of rules 10–19 is dirty.
            const prefix = `/rules/${id}`;
            return pendingPatches.some(
                ({path}) => path === prefix || path.startsWith(`${prefix}/`)
            );
        };
        const rules = this._model._policy ? policy.rules : policy.getRules();
        return rules.map((rule, idx) => {
            rule.id = idx;
            rule.dirty = hasPatchWithCurrentRule(rule, idx);
            rule.sevMap = this._model._policy
                ? {
                      CRITICAL: "Critical",
                      MAJOR: "Major",
                      MINOR: "Minor",
                      INFO: "Informational",
                  }
                : policy.getRuleSeverityMap();
            // TODO Replace static map by getRuleSourceMap()
            // Beware: this._model._policy if available is not an ConfigAuditPolicy entity
            // so getRuleSourceMap() is not available
            rule.sourceMap = this._model._policy
                ? {
                      LATEST: "Latest",
                      DEFAULT: "Default",
                  }
                : policy.getRuleSourceMap();
            rule.osMap = this._model._gameEditor.os;

            const commandSetEntityName =
                rule.commandSet !== "all" &&
                rule.commandSet !== "allConfig" &&
                rule.commandSet !== "allOperational"
                    ? this._model._commandSetFeed.getInstanceById(rule.commandSet) &&
                      this._model._commandSetFeed.getInstanceById(rule.commandSet).getId()
                    : rule.commandSet;
            rule.commandSet = commandSetEntityName;

            const commandResult = rule.commandSetResult;
            if (commandResult) {
                const commandRestype = commandResult.type;
                let commandResultVal = "all";

                if (commandRestype === "conceptItems") {
                    commandResultVal = commandResult.conceptItemId;
                } else if (commandRestype === "restSyncItems") {
                    commandResultVal = commandResult.restSyncItemId;
                } else if (commandRestype === "cliCommands") {
                    commandResultVal = rule.command;
                } else if (commandRestype === "basicMode") {
                    commandResultVal = "Standard";
                }

                rule.commandResult = commandResultVal;
            }

            rule.isReadOnly = this._model._isReadOnly;
            return rule;
        });
    }
    _prepareValues() {
        let values, updatedOn;
        if (!this._model._policy) {
            const properties = this.model.auditPolicy.getProperties();
            updatedOn = this._model._auditPolicy.updatedOn || 0;
            values = _.cloneDeep(properties);
        } else {
            values = _.cloneDeep(this._model._policy);
            updatedOn = this._model._policy.updatedOn || 0;
        }
        const lastCommitUpdatedOn = this._model._lastCommitUpdatedOn || 0;
        if (updatedOn > lastCommitUpdatedOn) {
            values.lastCommitStatus = "PENDING";
        } else {
            values.lastCommitStatus = values.lastCommitHash;
        }
        return values;
    }

    _getJtype() {
        let permissionLevels;
        if (this._model._permLevels.length === 0 || this._model._isReadOnly) {
            permissionLevels = this._model._policy
                ? {
                      auditManageLevel1: "1",
                      auditManageLevel2: "2",
                      auditManageLevel3: "3",
                  }
                : this._model._auditPolicy.getPermissionLevelLabels();
        } else {
            permissionLevels = _.pick(
                this._model._auditPolicy.getPermissionLevelLabels(),
                this._model._permLevels
            );
        }
        const jtype = {
            "name@Display.label": "Name",
            "name@Display.readOnly": this._model._isReadOnly,
            name: "",

            /* Update later as per entity field and convert to number input */
            "permissionLevel@Display.label": "Permission Level",
            "permissionLevel@Display.readOnly": this._model._isReadOnly,
            "permissionLevel@Required": true,
            permissionLevel: "auditManageLevel1",
            "permissionLevel@Enum": Object.keys(permissionLevels),
            "permissionLevel@Display.enum": Object.values(permissionLevels),

            "description@Display.label": "Description",
            "description@Display.readOnly": this._model._isReadOnly,
            description: "",
        };
        if (Steve.controlAppNav._versionControlLocalEnabled) {
            jtype["lastCommitStatus@Display.label"] = "Last Commit";
            jtype["lastCommitStatus@Display.readOnly"] = true;
            jtype["lastCommitStatus"] = "";
        }
        return jtype;
    }

    _registerRowMovePlugin(grid, dataView) {
        const moveRowsPlugin = new Slick.RowMoveManager({
            cancelEditOnDrag: true,
        });
        // const sortingComparator = this._model.CustomFieldsEntity.getSortingComparator("order");

        moveRowsPlugin.onBeforeMoveRows.subscribe(function (e, {insertBefore, rows}) {
            rows = [...rows].sort();
            if (
                _.first(rows) === insertBefore ||
                _.last(rows) === insertBefore - 1
                // _.get(dataView.getItem(insertBefore), "inherited")
            ) {
                e.stopPropagation();
                return false;
            }
            return true;
        });
        moveRowsPlugin.onMoveRows.subscribe(function (e, args) {
            let extractedRows = [],
                left,
                right;
            const rows = args.rows;
            const insertBefore = args.insertBefore;
            let data = dataView.getItems();
            left = data.slice(0, insertBefore);
            right = data.slice(insertBefore, data.length);

            rows.sort(function (a, b) {
                return a - b;
            });

            for (let i = 0; i < rows.length; i++) {
                extractedRows.push(data[rows[i]]);
            }

            rows.reverse();

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                if (row < insertBefore) {
                    left.splice(row, 1);
                } else {
                    right.splice(row - insertBefore, 1);
                }
            }

            data = left.concat(extractedRows.concat(right));

            const selectedRows = [];
            for (let i = 0; i < rows.length; i++) {
                selectedRows.push(left.length + i);
            }
            dataView.setItems(data);
            grid.resetActiveCell();
            grid.setSelectedRows(selectedRows);
            grid.invalidateAllRows();
            grid.resizeCanvas();
        });

        grid.registerPlugin(moveRowsPlugin);
    }

    _createNoDataPlaceholder(/*msg, veil = false*/) {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = "No Rules to display";
        return noDataPlaceholder;
    }

    _toggleNoDataPlaceholder(state) {
        this._slickTableContainer.classList.toggle("empty", state);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = AuditPolicyDetailsViewRenderer;
